<template>
    <div class="main-content">
        <div class="container">
            <!-- Header Section -->
            <div class="header-section">
                <div class="back-link">
                    <a href="/#/spatial/list"><i class="el-icon-back"></i> Back to Datasets</a>
                </div>
                <h1>{{ detailInfo.name || 'Dataset Detail' }}</h1>
                <p class="lead">Detailed analysis and visualization for this spatial dataset.</p>
            </div>

            <!-- Overview Card -->
            <div class="info-card">
                <div class="card-header">
                    <div class="title-row">
                        <span><i class="el-icon-info"></i> Dataset Overview</span>
                    </div>
                </div>
                <div class="info-grid">
                    <div class="info-item">
                        <span class="label"><i class="el-icon-price-tag"></i> Name</span>
                        <span class="value highlight">{{ detailInfo.name }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label"><i class="el-icon-user"></i> Organism</span>
                        <span class="value">{{ detailInfo.organism }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label"><i class="el-icon-s-operation"></i> Tissue</span>
                        <span class="value">{{ detailInfo.tissue }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label"><i class="el-icon-first-aid-kit"></i> Condition</span>
                        <span class="value">{{ detailInfo.condition }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label"><i class="el-icon-office-building"></i> Data Source</span>
                        <span class="value">{{ detailInfo.dataSource }}</span>
                    </div>
                    <div class="info-item">
                        <span class="label"><i class="el-icon-link"></i> Accession</span>
                        <span class="value">
                            <span v-if="!detailInfo.dataLink">{{ detailInfo.dataAccession }}</span>
                            <a v-else :href="detailInfo.dataLink" target="_blank">{{ detailInfo.dataAccession }}</a>
                        </span>
                    </div>
                    <div class="info-item">
                        <span class="label"><i class="el-icon-document"></i> PMID</span>
                        <span class="value">
                            <a v-if="detailInfo.pmid" :href="`https://pubmed.ncbi.nlm.nih.gov/${detailInfo.pmid}`" target="_blank">{{ detailInfo.pmid }}</a>
                            <span v-else>-</span>
                        </span>
                    </div>
                    <div class="info-item" v-if="detailInfo.resolution">
                        <span class="label"><i class="el-icon-s-data"></i> Resolution</span>
                        <span class="value">{{ detailInfo.resolution }}</span>
                    </div>
                    <div class="info-item" v-if="detailInfo.paper">
                        <span class="label"><i class="el-icon-reading"></i> Paper</span>
                        <span class="value">{{ detailInfo.paper }}</span>
                    </div>
                    <div class="info-item" v-if="detailInfo.notes">
                        <span class="label"><i class="el-icon-notebook-1"></i> Notes</span>
                        <span class="value">{{ detailInfo.notes }}</span>
                    </div>
                </div>
            </div>

            <el-tabs v-model="activeTab" class="mt-4 custom-tabs" style="margin-bottom: 20px;">
                <el-tab-pane label="Gene" name="Gene">
                    <div class="section-container">
                        <h3 class="section-title"><i class="el-icon-picture-outline"></i> Spatial Overview</h3>
                        <el-row :gutter="24">
                            <el-col :span="24">
                                <div class="resource-card" v-if="detailInfo.spatialOverview">
                                    <div class="resource-body">
                                        <el-image
                                            :src="getFullPath(detailInfo.spatialOverview)"
                                            class="resource-image"
                                            fit="contain"
                                            :preview-src-list="[getFullPath(detailInfo.spatialOverview)]">
                                        </el-image>
                                    </div>
                                    <div class="resource-footer">Spatial Overview</div>
                                </div>
                                <div v-else class="no-data">
                                    No spatial overview image available.
                                </div>
                            </el-col>
                        </el-row>
                    </div>

                    <div class="section-container mt-4">
                        <h3 class="section-title"><i class="el-icon-s-grid"></i> Gene Correlation</h3>

                        <!-- Filters -->
                        <div class="filter-bar">
                            <el-form :inline="true" :model="geneFilters" size="small">
                                <el-form-item label="Cell Type">
                                    <el-select v-model="geneFilters.cellType" placeholder="Select Cell Type" clearable filterable>
                                        <el-option v-for="item in filterOptions.cellTypes" :key="item" :label="item" :value="item"></el-option>
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="Gene 1">
                                    <el-select v-model="geneFilters.gene1" placeholder="Select Gene 1" clearable filterable>
                                        <el-option v-for="item in filterOptions.gene1" :key="item" :label="item" :value="item"></el-option>
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="Gene 2">
                                    <el-select v-model="geneFilters.gene2" placeholder="Select Gene 2" clearable filterable>
                                        <el-option v-for="item in filterOptions.gene2" :key="item" :label="item" :value="item"></el-option>
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="Max Adj. P">
                                    <el-input v-model="geneFilters.adjusted_p" placeholder="e.g. 0.05" clearable></el-input>
                                </el-form-item>
                                <el-form-item label="Min Corr">
                                    <el-input v-model="geneFilters.corr" placeholder="e.g. 0.3" clearable></el-input>
                                </el-form-item>
                                <el-form-item>
                                    <el-button type="primary" @click="handleGeneFilterSearch" icon="el-icon-search">Search</el-button>
                                    <el-button @click="handleGeneFilterReset" icon="el-icon-refresh">Reset</el-button>
                                    <el-button type="warning" @click="handleGeneExport" icon="el-icon-download">Download</el-button>
                                </el-form-item>
                            </el-form>
                        </div>

                        <!-- Table -->
                        <div class="table-card plain">
                            <el-table :data="geneTableData" style="width: 100%" stripe border size="small">
                                <el-table-column prop="gene1" label="Gene 1" sortable min-width="100">
                                    <template slot-scope="scope">
                                        <a href="javascript:void(0)" class="gene-link" @click="handleGenePreview(scope.row.gene1)">{{ scope.row.gene1 }}</a>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="gene2" label="Gene 2" sortable min-width="100">
                                    <template slot-scope="scope">
                                        <a href="javascript:void(0)" class="gene-link" @click="handleGenePreview(scope.row.gene2)">{{ scope.row.gene2 }}</a>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="pvalue" label="P-value" sortable min-width="100" />
                                <el-table-column prop="adjustedP" label="Adjusted P" sortable min-width="110" />
                                <el-table-column prop="corr" label="Correlation" sortable min-width="110" />
                                <el-table-column prop="cellType" label="Cell Type" sortable min-width="120" />
                                <el-table-column prop="tissue" label="Tissue" sortable min-width="100" />
                                <el-table-column prop="data" label="Data" sortable min-width="100" />
                                <el-table-column prop="type" label="Type" sortable min-width="100" />
                            </el-table>

                            <div class="pagination-wrapper">
                                <el-pagination
                                    @size-change="handleGeneSizeChange"
                                    @current-change="handleGenePageChange"
                                    :current-page="genePagination.pageNum"
                                    :page-sizes="[10, 20, 50]"
                                    :page-size="genePagination.pageSize"
                                    layout="total, sizes, prev, pager, next"
                                    :total="genePagination.total">
                                </el-pagination>
                            </div>
                        </div>
                    </div>

                    <!-- Draw Section -->
                    <div class="section-container mt-4" style="margin-top: 20px;">
                        <h3 class="section-title"><i class="el-icon-edit-outline"></i> Draw Function</h3>
                        <div class="panel">
                            <el-form :inline="true" :model="drawForm" class="demo-form-inline">
                                <el-form-item label="Gene 1">
                                    <el-select
                                        v-model="drawForm.gene1"
                                        filterable
                                        placeholder="Select Gene 1">
                                        <el-option
                                            v-for="item in filterOptions.gene1"
                                            :key="item"
                                            :label="item"
                                            :value="item">
                                        </el-option>
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="Gene 2">
                                    <el-select
                                        v-model="drawForm.gene2"
                                        filterable
                                        placeholder="Select Gene 2">
                                        <el-option
                                            v-for="item in filterOptions.gene2"
                                            :key="item"
                                            :label="item"
                                            :value="item">
                                        </el-option>
                                    </el-select>
                                </el-form-item>
                                <el-form-item label="Cell Type">
                                    <el-select v-model="drawForm.cellType" placeholder="Select Cell Type">
                                        <el-option v-for="item in filterOptions.cellTypes" :key="item" :label="item" :value="item"></el-option>
                                    </el-select>
                                </el-form-item>
                                <el-form-item>
                                    <el-button type="primary" @click="handleDraw" :loading="drawLoading">Draw</el-button>
                                </el-form-item>
                            </el-form>

                            <div v-if="drawResultImage" class="draw-result mt-4" style="text-align: center;">
                                <el-image
                                    :src="getFullPath(drawResultImage)"
                                    style="max-height: 600px; max-width: 100%;"
                                    fit="contain"
                                    :preview-src-list="[getFullPath(drawResultImage)]">
                                    <div slot="placeholder" class="image-slot">
                                        Loading<span class="dot">...</span>
                                    </div>
                                </el-image>
                            </div>
                        </div>
                    </div>

                    <!-- Gene Image Preview Dialog -->
                    <el-dialog :visible.sync="genePreviewVisible" title="Gene Preview" width="50%" append-to-body>
                        <div v-if="genePreviewUrl" style="text-align: center;">
                             <el-image :src="getFullPath(genePreviewUrl)" fit="contain">
                                <div slot="placeholder" class="image-slot">
                                    Loading<span class="dot">...</span>
                                </div>
                            </el-image>
                        </div>
                    </el-dialog>
                </el-tab-pane>
                <el-tab-pane label="Module" name="Module">
                    <!-- Module Summary Table -->
                    <div class="section-container mt-4">
                        <h3 class="section-title"><i class="el-icon-s-grid"></i> Module Summary</h3>
                        
                        <!-- Module Filters -->
                        <div class="filter-container mb-3">
                            <el-form :inline="true" :model="moduleFilters" size="small">
                                <el-form-item label="Cell Type">
                                    <el-select v-model="moduleFilters.cellType" placeholder="Select Cell Type" clearable filterable @change="handleModuleSummarySearch">
                                        <el-option v-for="type in moduleFilterOptions.cellTypes" :key="type" :label="type" :value="type" />
                                    </el-select>
                                </el-form-item>
                                <el-form-item>
                                    <el-button type="primary" @click="handleModuleSummarySearch" icon="el-icon-search">Search</el-button>
                                    <el-button @click="handleModuleSummaryReset" icon="el-icon-refresh">Reset</el-button>
                                </el-form-item>
                            </el-form>
                        </div>

                        <div class="table-card">
                            <el-table :data="moduleSummaryTableData" style="width: 100%" stripe>
                                <el-table-column prop="data" label="Data" sortable />
                                <el-table-column prop="moduleName" label="Module_Name" sortable>
                                    <template slot-scope="scope">
                                        <el-button type="text" @click="handleModuleClick(scope.row)">{{ scope.row.moduleName }}</el-button>
                                    </template>
                                </el-table-column>
                                <el-table-column prop="cellType" label="Cell_Type" sortable />
                                <el-table-column prop="numberOfGenes" label="Number_of_Genes" sortable />
                                <el-table-column prop="topGo" label="Top_GO" show-overflow-tooltip />
                                <el-table-column prop="topKegg" label="Top_Kegg" show-overflow-tooltip />
                                <el-table-column prop="topTf" label="Top_TF" show-overflow-tooltip />
                            </el-table>
                            <div class="pagination-wrapper">
                                <el-pagination
                                    @size-change="handleModuleSummarySizeChange"
                                    @current-change="handleModuleSummaryPageChange"
                                    :current-page="moduleSummaryPagination.pageNum"
                                    :page-sizes="[10, 20, 50]"
                                    :page-size="moduleSummaryPagination.pageSize"
                                    layout="total, sizes, prev, pager, next"
                                    :total="moduleSummaryPagination.total">
                                </el-pagination>
                            </div>
                        </div>
                    </div>

                    <!-- Module Specific Detail Section -->
                    <transition name="el-fade-in">
                        <div id="module-detail-section" v-if="moduleDetailData || moduleDetailLoading" class="mt-4" v-loading="moduleDetailLoading">
                            <!-- Header -->
                            <div class="section-header" v-if="currentModuleRow">
                                <h3 class="section-title">Module Detail: {{ currentModuleRow.moduleName }}</h3>
                            </div>

                            <div v-if="moduleDetailData">
                                <div class="sub-section mt-4">
                                    <div class="section-header" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                                        <h4 class="sub-title" style="margin: 0;">Gene Correlation</h4>
                                        <div class="header-actions">
                                            <el-button v-if="moduleDetailData.geneTableDownloadUrl" type="text"
                                                @click="downloadFile(moduleDetailData.geneTableDownloadUrl)" icon="el-icon-download">
                                                Download
                                            </el-button>
                                        </div>
                                    </div>
                                    <div class="table-card plain">
                                        <el-table :data="pagedModuleGeneData" style="width: 100%" stripe border size="small">
                                            <el-table-column prop="geneSymbol" label="Gene.Symbol" sortable>
                                                <template slot-scope="scope">
                                                    <a :href="`/#/gene/detail/${scope.row.geneId}?species=${detailInfo.organism}`" target="_blank" class="gene-link">{{ scope.row.geneSymbol }}</a>
                                                </template>
                                            </el-table-column>
                                            <el-table-column prop="module" label="module" sortable />
                                            <el-table-column prop="cellType" label="cell_type" sortable />
                                            <el-table-column prop="ensemblId" label="Ensembl.ID" />
                                            <el-table-column prop="geneLocation" label="Gene.Location" />
                                            <el-table-column prop="geneType" label="Gene.Type" />
                                        </el-table>
                                        <div class="pagination-wrapper">
                                            <el-pagination
                                                @size-change="handleModuleGeneSizeChange"
                                                @current-change="handleModuleGenePageChange"
                                                :current-page="moduleGenePagination.currentPage"
                                                :page-sizes="[10, 20, 50]"
                                                :page-size="moduleGenePagination.pageSize"
                                                layout="total, sizes, prev, pager, next"
                                                :total="moduleGenePagination.total">
                                            </el-pagination>
                                        </div>
                                    </div>
                                </div>

                                <!-- 2. Charts (GO & KEGG) -->
                                <div class="sub-section mt-4">
                                    <h4 class="sub-title" style="margin-bottom: 15px;">Pathway Enrichment</h4>
                                    <el-row :gutter="20">
                                        <el-col :span="12">
                                            <div class="chart-card">
                                                <div class="chart-header" style="display: flex; justify-content: space-between; align-items: center; padding: 10px;">
                                                    <span style="font-weight: bold;">GO Barplot</span>
                                                    <el-button v-if="moduleDetailData.goResultsDownloadUrl" type="text" size="small"
                                                        @click="downloadFile(moduleDetailData.goResultsDownloadUrl)" icon="el-icon-download">
                                                        Download Results
                                                    </el-button>
                                                </div>
                                                <div class="chart-content" style="text-align: center; padding: 10px;">
                                                    <el-image
                                                        v-if="moduleDetailData.goBarplotUrl"
                                                        :src="getFullPath(moduleDetailData.goBarplotUrl)"
                                                        fit="contain"
                                                        style="max-height: 400px;"
                                                        :preview-src-list="[getFullPath(moduleDetailData.goBarplotUrl)]">
                                                    </el-image>
                                                    <div v-else class="no-data">No Image Available</div>
                                                </div>
                                            </div>
                                        </el-col>
                                        <el-col :span="12">
                                            <div class="chart-card">
                                                <div class="chart-header" style="display: flex; justify-content: space-between; align-items: center; padding: 10px;">
                                                    <span style="font-weight: bold;">KEGG Barplot</span>
                                                    <el-button v-if="moduleDetailData.keggResultsDownloadUrl" type="text" size="small"
                                                        @click="downloadFile(moduleDetailData.keggResultsDownloadUrl)" icon="el-icon-download">
                                                        Download Results
                                                    </el-button>
                                                </div>
                                                <div class="chart-content" style="text-align: center; padding: 10px;">
                                                     <el-image
                                                        v-if="moduleDetailData.keggBarplotUrl"
                                                        :src="getFullPath(moduleDetailData.keggBarplotUrl)"
                                                        fit="contain"
                                                        style="max-height: 400px;"
                                                        :preview-src-list="[getFullPath(moduleDetailData.keggBarplotUrl)]">
                                                    </el-image>
                                                    <div v-else class="no-data">No Image Available</div>
                                                </div>
                                            </div>
                                        </el-col>
                                    </el-row>
                                </div>

                                <!-- 3. Enriched TF/Motif Table -->
                                <div class="sub-section mt-4">
                                    <div class="section-header" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                                        <h4 class="sub-title" style="margin: 0;">Enriched TF/Motif</h4>
                                        <div class="header-actions">
                                            <el-button v-if="moduleDetailData.tfTableDownloadUrl" type="text"
                                                @click="downloadFile(moduleDetailData.tfTableDownloadUrl)" icon="el-icon-download">
                                                Download
                                            </el-button>
                                        </div>
                                    </div>
                                    <div class="table-card plain" >
                                        <el-table :data="pagedModuleTfData" style="width: 100%" stripe border size="small">
                                            <el-table-column prop="motif" label="motif" sortable />
                                            <el-table-column prop="nes" label="NES" sortable />
                                            <el-table-column prop="auc" label="AUC" sortable />
                                            <el-table-column prop="tfHighConf" label="TF_highConf" sortable />
                                            <el-table-column prop="tfLowConf" label="TF_lowConf" sortable />
                                            <el-table-column prop="nEnrGenes" label="nEnrGenes" sortable />
                                            <el-table-column prop="rankAtMax" label="rankAtMax" sortable />
                                            <el-table-column prop="enrichedGenes" label="enrichedGenes" show-overflow-tooltip />
                                        </el-table>
                                        <div class="pagination-wrapper">
                                            <el-pagination
                                                @size-change="handleModuleTfSizeChange"
                                                @current-change="handleModuleTfPageChange"
                                                :current-page="moduleTfPagination.currentPage"
                                                :page-sizes="[10, 20, 50]"
                                                :page-size="moduleTfPagination.pageSize"
                                                layout="total, sizes, prev, pager, next"
                                                :total="moduleTfPagination.total">
                                            </el-pagination>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </transition>
                </el-tab-pane>
            </el-tabs>
        </div>
    </div>
</template>

<script>
export default {
    name: "SpatialDetail",
    data() {
        return {
            detailInfo: {},
            activeTab: 'Gene',
            geneFilters: {
                cellType: '',
                gene1: '',
                gene2: '',
                adjusted_p: '',
                corr: ''
            },
            filterOptions: {
                cellTypes: [],
                gene1: [],
                gene2: []
            },
            geneTableData: [],
            genePagination: {
                pageNum: 1,
                pageSize: 10,
                total: 0
            },
            drawForm: {
                gene1: '',
                gene2: '',
                cellType: ''
            },
            drawLoading: false,
            drawResultImage: '',
            genePreviewVisible: false,
            genePreviewUrl: '',

            // Module Summary Data
            moduleSummaryTableData: [],
            moduleSummaryPagination: {
                pageNum: 1,
                pageSize: 10,
                total: 0
            },
            moduleFilters: {
                cellType: ''
            },
            moduleFilterOptions: {
                cellTypes: []
            },

            // Module Detail Data
            moduleDetailData: null,
            moduleDetailLoading: false,
            currentModuleRow: null,

            // Module Gene Table Pagination (Frontend)
            moduleGenePagination: {
                currentPage: 1,
                pageSize: 10,
                total: 0
            },

            // Module TF Table Pagination (Frontend)
            moduleTfPagination: {
                currentPage: 1,
                pageSize: 10,
                total: 0
            }
        };
    },
    computed: {
        pagedModuleGeneData() {
            if (!this.moduleDetailData || !this.moduleDetailData.geneTable) return [];
            const start = (this.moduleGenePagination.currentPage - 1) * this.moduleGenePagination.pageSize;
            const end = start + this.moduleGenePagination.pageSize;
            return this.moduleDetailData.geneTable.slice(start, end);
        },
        pagedModuleTfData() {
            if (!this.moduleDetailData || !this.moduleDetailData.tfTable) return [];
            const start = (this.moduleTfPagination.currentPage - 1) * this.moduleTfPagination.pageSize;
            const end = start + this.moduleTfPagination.pageSize;
            return this.moduleDetailData.tfTable.slice(start, end);
        }
    },
    created() {
        this.getDetail();
        this.getFilterOptions();
        this.getModuleFilterOptions();
        this.getGeneData();
        this.getModuleSummaryData();
    },
    methods: {
        async getDetail() {
            try {
                const res = await this.$ajax.get(`/spatial/detail/${this.$route.params.id}`);
                if (res.code === 200) {
                    this.detailInfo = res.data;
                } else {
                    this.$message.error(res.message || 'Failed to fetch detail');
                }
            } catch (error) {
                console.error("Failed to fetch spatial detail:", error);
                this.$message.error("Failed to fetch spatial detail");
            }
        },

        async getFilterOptions() {
            try {
                const res = await this.$ajax.get(`/spatial/corr/filters?spatialId=${this.$route.params.id}`);
                if (res.code === 200) {
                    this.filterOptions = res.data;
                }
            } catch (error) {
                console.error("Failed to fetch filter options:", error);
            }
        },

        async getGeneData() {
            try {
                const params = new URLSearchParams();
                params.append('pageNum', this.genePagination.pageNum);
                params.append('pageSize', this.genePagination.pageSize);
                params.append('spatialId', this.$route.params.id);

                if (this.geneFilters.adjusted_p) params.append('maxAdjustedP', this.geneFilters.adjusted_p);
                if (this.geneFilters.corr) params.append('minCorr', this.geneFilters.corr);
                if (this.geneFilters.cellType) params.append('cellType', this.geneFilters.cellType);
                // Sending both gene1 and gene2 as per UI, though user prompt mentioned 'gene'.
                // If backend only supports 'gene', this might need adjustment, but usually filter specific columns is preferred.
                if (this.geneFilters.gene1) params.append('gene1', this.geneFilters.gene1);
                if (this.geneFilters.gene2) params.append('gene2', this.geneFilters.gene2);

                const res = await this.$ajax.get(`/spatial/corr/page?${params.toString()}`);
                if (res.code === 200) {
                    this.geneTableData = res.data.records || [];
                    this.genePagination.total = res.data.total || 0;
                }
            } catch (error) {
                console.error("Failed to fetch gene table data:", error);
            }
        },

        handleGeneFilterSearch() {
            this.genePagination.pageNum = 1;
            this.getGeneData();
        },

        handleGeneFilterReset() {
            this.geneFilters = {
                cellType: '',
                gene1: '',
                gene2: '',
                adjusted_p: '',
                corr: ''
            };
            this.handleGeneFilterSearch();
        },

        handleGeneSizeChange(size) {
            this.genePagination.pageSize = size;
            this.getGeneData();
        },

        handleGenePageChange(page) {
            this.genePagination.pageNum = page;
            this.getGeneData();
        },

        handleGeneExport() {
            const params = new URLSearchParams();
            params.append('spatialId', this.$route.params.id);
            if (this.geneFilters.adjusted_p) params.append('maxAdjustedP', this.geneFilters.adjusted_p);
            if (this.geneFilters.corr) params.append('minCorr', this.geneFilters.corr);
            if (this.geneFilters.cellType) params.append('cellType', this.geneFilters.cellType);
            if (this.geneFilters.gene1) params.append('gene1', this.geneFilters.gene1);
            if (this.geneFilters.gene2) params.append('gene2', this.geneFilters.gene2);

            const url = `/api/spatial/corr/export?${params.toString()}`;
            window.open(url, '_blank');
        },

        handleGenePreview(gene) {
            if (!gene) return;
            // Path: /file/common?path=/spatial/:spatialName/fig/gene/$gene.png
            const spatialName = this.detailInfo.name;
            if (!spatialName) {
                this.$message.warning('Spatial dataset name is missing');
                return;
            }
            this.genePreviewUrl = `/api/file/common?path=/spatial/${spatialName}/fig/gene/${gene}.png`;
            this.genePreviewVisible = true;
        },

        async handleDraw() {
            if (!this.drawForm.gene1 || !this.drawForm.gene2 || !this.drawForm.cellType) {
                this.$message.warning('Please select Gene 1, Gene 2 and Cell Type');
                return;
            }

            this.drawLoading = true;
            try {
                const params = {
                    gene1: this.drawForm.gene1,
                    gene2: this.drawForm.gene2,
                    cellType: this.drawForm.cellType,
                    spatial: this.detailInfo.name
                };

                const res = await this.$ajax.post('/spatial/draw', params);

                if (res.code === 200) {
                    this.drawResultImage = res.data;
                } else {
                    this.$message.error(res.message || 'Draw failed');
                }
            } catch (error) {
                console.error(error);
                this.$message.error('An error occurred during drawing');
            } finally {
                this.drawLoading = false;
            }
        },

        async getModuleSummaryData() {
            try {
                const params = new URLSearchParams();
                params.append('pageNum', this.moduleSummaryPagination.pageNum);
                params.append('pageSize', this.moduleSummaryPagination.pageSize);
                params.append('spatialId', this.$route.params.id);
                if (this.moduleFilters.cellType) params.append('cellType', this.moduleFilters.cellType);

                const res = await this.$ajax.get(`/spatial/module/summary/page?${params.toString()}`);
                if (res.code === 200) {
                    this.moduleSummaryTableData = res.data.records || [];
                    this.moduleSummaryPagination.total = res.data.total || 0;
                }
            } catch (error) {
                console.error("Failed to fetch module summary data:", error);
            }
        },

        async getModuleFilterOptions() {
            try {
                const res = await this.$ajax.get(`/spatial/module/filters?spatialId=${this.$route.params.id}`);
                if (res.code === 200) {
                    this.moduleFilterOptions = res.data;
                }
            } catch (error) {
                console.error("Failed to fetch module filter options:", error);
            }
        },

        handleModuleSummarySearch() {
            this.moduleSummaryPagination.pageNum = 1;
            this.getModuleSummaryData();
        },

        handleModuleSummaryReset() {
            this.moduleFilters.cellType = '';
            this.handleModuleSummarySearch();
        },

        handleModuleSummarySizeChange(size) {
            this.moduleSummaryPagination.pageSize = size;
            this.getModuleSummaryData();
        },

        handleModuleSummaryPageChange(page) {
            this.moduleSummaryPagination.pageNum = page;
            this.getModuleSummaryData();
        },

        handleModuleClick(row) {
            this.currentModuleRow = row;
            this.moduleDetailData = null; // Clear previous data
            this.getModuleDetailData(row);

            // Scroll to detail section
            this.$nextTick(() => {
                const element = document.getElementById('module-detail-section');
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            });
        },

        async getModuleDetailData(row) {
            this.moduleDetailLoading = true;
            try {
                const params = new URLSearchParams();
                params.append('spatialId', this.$route.params.id);
                params.append('cellType', row.cellType);
                let moduleNum = row.moduleName;
                const match = row.moduleName.match(/\d+/);
                if (match) {
                    moduleNum = match[0];
                }
                params.append('moduleNum', moduleNum);

                const res = await this.$ajax.get(`/spatial/module/detail?${params.toString()}`);
                if (res.code === 200) {
                    this.moduleDetailData = res.data;
                    // Reset pagination for sub-tables
                    this.moduleGenePagination.currentPage = 1;
                    this.moduleGenePagination.total = res.data.geneTable ? res.data.geneTable.length : 0;

                    this.moduleTfPagination.currentPage = 1;
                    this.moduleTfPagination.total = res.data.tfTable ? res.data.tfTable.length : 0;
                } else {
                    this.$message.error(res.message || 'Failed to fetch module details');
                }
            } catch (error) {
                console.error("Failed to fetch module details:", error);
                this.$message.error("Failed to fetch module details");
            } finally {
                this.moduleDetailLoading = false;
            }
        },

        handleModuleGeneSizeChange(size) {
            this.moduleGenePagination.pageSize = size;
            this.moduleGenePagination.currentPage = 1;
        },

        handleModuleGenePageChange(page) {
            this.moduleGenePagination.currentPage = page;
        },

        handleModuleTfSizeChange(size) {
            this.moduleTfPagination.pageSize = size;
            this.moduleTfPagination.currentPage = 1;
        },

        handleModuleTfPageChange(page) {
            this.moduleTfPagination.currentPage = page;
        },

        downloadFile(url) {
            if (!url) {
                this.$message.warning('Download URL not available');
                return;
            }
            window.open(this.getFullPath(url), '_blank');
        },

        getFullPath(path) {
            if (!path) {
                return '';
            }
            if (path.startsWith('http') || path.startsWith('//')) {
                return path;
            }
            const baseUrl = window.location.origin;
            const normalizedPath = path.startsWith('/') ? path : `/${path}`;
            return `${baseUrl}${normalizedPath}`;
        }
    }
};
</script>

<style lang="less" scoped>
:root {
    --bg: #dfe4ed;
    --panel: #f7f9fc;
    --navy: #0e1a28;
    --muted: #4a586b;
    --accent: #5d87b5;
    --border: #c7d1dd;
    --shadow: 0 20px 44px rgba(14, 26, 40, 0.18);
    --primary: #0e1a28;
}

.main-content {
    background: #dfe4ed;
    min-height: 100vh;
    padding-bottom: 60px;
    font-family: 'Inter', sans-serif;
    color: #0e1a28;
    padding-top: 30px;
}

.container {
    padding: 0 20px;
}

/* Header Section */
.header-section {
    padding: 30px 0 20px;
    text-align: center;

    .back-link {
        margin-bottom: 12px;
        text-align: left;
        a {
            color: var(--accent);
            text-decoration: none;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 6px;
            transition: transform 0.2s;
            &:hover {
                transform: translateX(-4px);
            }
        }
    }

    h1 {
        font-family: 'Playfair Display', serif;
        font-size: 36px;
        color: var(--navy);
        margin: 0 0 8px;
        font-weight: 700;
        letter-spacing: -0.5px;
    }

    .lead {
        font-size: 16px;
        color: var(--muted);
        max-width: 700px;
        margin: 0 auto;
        line-height: 1.5;
    }
}

/* Info Card */
.info-card {
    background: white;
    border-radius: 20px;
    border: 1px solid var(--border);
    box-shadow: var(--shadow);
    padding: 24px;
    margin-bottom: 24px;

    .card-header {
        font-family: 'Playfair Display', serif;
        font-size: 20px;
        font-weight: 700;
        color: var(--navy);
        margin-bottom: 16px;
        display: flex;
        align-items: center;
        gap: 10px;
        i { color: var(--accent); }
    }

    .info-grid {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .info-item {
            display: flex;
            flex-direction: row;
            align-items: baseline;
            gap: 20px;
            width: 100%;

            .label {
                font-size: 14px;
                font-weight: 600;
                color: var(--muted);
                width: 140px;
                flex-shrink: 0;
                display: flex;
                align-items: center;
                gap: 8px;

                i {
                    color: var(--accent);
                    opacity: 0.8;
                }
            }

            .value {
                flex: 1;
                font-size: 15px;
                color: var(--navy);
                line-height: 1.6;
                word-break: break-word;

                &.highlight {
                    font-weight: 600;
                    color: var(--primary);
                    font-size: 16px;
                }

                a {
                    color: var(--accent);
                    text-decoration: none;
                    &:hover { text-decoration: underline; }
                }
            }
        }
    }
}

/* Section Styling */
.section-container {
    background: white;
    border-radius: 20px;
    border: 1px solid var(--border);
    padding: 24px;
    box-shadow: var(--shadow);

    .section-title {
        font-family: 'Playfair Display', serif;
        font-size: 24px;
        font-weight: 700;
        color: var(--navy);
        margin: 0 0 16px;
        display: flex;
        align-items: center;
        gap: 12px;
        i { color: var(--accent); }
    }
}

/* Resource Cards */
.resource-card {
    background: white;
    border-radius: 15px;
    border: 1px solid var(--border);
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0,0,0,0.05);
    margin-bottom: 20px;
    transition: transform 0.3s;

    &:hover {
        transform: translateY(-5px);
    }

    .resource-body {
        background: white;
        padding: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 500px;
        overflow: hidden;

        .resource-image {
            width: 100%;
            height: 100%;
        }
    }

    .resource-footer {
        padding: 14px 20px;
        background: white;
        font-weight: 700;
        color: var(--navy);
        border-top: 1px solid var(--border);
        display: flex;
        justify-content: center;
        align-items: center;
    }
}

/* Table Card */
.table-card {
    background: white;
    border-radius: 20px;
    border: 1px solid var(--border);
    box-shadow: var(--shadow);
    overflow: hidden;
    padding: 16px;

    &.plain {
        box-shadow: none;
        padding: 0;
        border: none;
        background: transparent;
    }
}

/* Pagination Wrapper */
.pagination-wrapper {
    margin-top: 12px;
    text-align: right;
    padding-top: 10px;
}

/* Chart Card */
.chart-card {
    background: white;
    border-radius: 16px;
    border: 1px solid var(--border);
    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
    overflow: hidden;
    transition: transform 0.2s, box-shadow 0.2s;
    height: 100%;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0,0,0,0.1);
    }

    .chart-header {
        padding: 16px 20px;
        background: #f8fafc;
        border-bottom: 1px solid var(--border);
        font-weight: 600;
        color: var(--navy);
    }

    .chart-content {
        padding: 20px;
        min-height: 300px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: white;
    }
}

/* Custom Table Overrides */
::v-deep .el-table {
    background: transparent;
    font-size: 15px; /* Base font size increased */

    &::before { display: none; }

    th {
        background: var(--panel) !important;
        color: var(--navy);
        font-weight: 700;
        font-family: 'Playfair Display', serif;
        padding: 16px 0;
        border-bottom: 1px solid var(--border);
        font-size: 16px; /* Header font size */
    }

    td {
        padding: 14px 0;
        border-bottom: 1px solid var(--border);
    }

    .highlight-row {
        background-color: rgba(93, 135, 181, 0.1) !important;
    }

    tr:hover td {
        background-color: rgba(93, 135, 181, 0.05) !important;
    }
}

/* Links */
.gene-link {
    color: var(--accent);
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
}

/* Custom Tabs - Clean Style */
.custom-tabs {
    /deep/ .el-tabs__header {
        margin-bottom: 24px;
        border-bottom: 1px solid var(--border);
    }

    /deep/ .el-tabs__nav-wrap::after {
        display: none;
    }

    /deep/ .el-tabs__item {
        font-size: 16px;
        font-weight: 500;
        color: var(--muted);
        padding: 0 24px !important;
        height: 48px;
        line-height: 48px;
        transition: all 0.3s;

        &:hover {
            color: var(--accent);
        }

        &.is-active {
            color: var(--navy);
            font-weight: 600;
        }
    }

    /deep/ .el-tabs__active-bar {
        background-color: var(--accent);
        height: 3px;
    }
}

.no-data {
    padding: 40px;
    text-align: center;
    color: var(--muted);
    font-style: italic;
}

.mt-4 {
    margin-top: 24px;
}
</style>
